/**
 * ProofRequirementBlock — operator-locked instructions explaining
 * EXACTLY what the learner needs to upload to count a proof.
 *
 * Operator brief (Feb 13, 2026): forward-facing camera, ~20–30 min
 * continuous drive, AI finds proof moments via timestamps. Same
 * location max 2× toward proof. Retained foundations must hold.
 *
 * Renders a compact, calm block above the upload CTA so the learner
 * never has to guess what's expected. Displayed on both
 * `prove_next` and `transfer_next` focus cards.
 */
import { Camera, Clock, MapPin, ShieldCheck } from "lucide-react";


export default function ProofRequirementBlock({
  competencyLabel,
  currentFocusLabel,
  retainedLabels = [],
  verifiedCount = 0,
  requiredCount = 0,
}) {
  const remaining = Math.max(0, (requiredCount || 0) - (verifiedCount || 0));
  return (
    <div
      className="mt-4 rounded-2xl bg-white border border-slate-200 p-4"
      data-testid="proof-requirement-block"
    >
      <div className="text-[11px] font-bold tracking-[0.12em] text-slate-500 mb-2">
        RECORDING ADVISORY EVIDENCE
      </div>

      <ul className="space-y-2 text-sm">
        <li className="flex items-start gap-2"
            data-testid="proof-requirement-camera">
          <Camera size={14} className="text-slate-500 mt-0.5 flex-shrink-0" />
          <span className="text-slate-700">
            Forward-facing camera, mounted on the dashboard.
          </span>
        </li>
        <li className="flex items-start gap-2"
            data-testid="proof-requirement-duration">
          <Clock size={14} className="text-slate-500 mt-0.5 flex-shrink-0" />
          <span className="text-slate-700">
            One continuous drive, around 20–30 minutes.
          </span>
        </li>
        <li className="flex items-start gap-2"
            data-testid="proof-requirement-focus">
          <span className="w-3.5 h-3.5 rounded-full bg-emerald-500 mt-1 flex-shrink-0" />
          <span className="text-slate-700">
            Show clear examples of <strong>{currentFocusLabel || competencyLabel}</strong>{" "}
            ({remaining} more advisory {remaining === 1 ? "observation" : "observations"} toward
            the coaching target of {requiredCount}).
          </span>
        </li>
        {retainedLabels.length > 0 && (
          <li className="flex items-start gap-2"
              data-testid="proof-requirement-retained">
            <ShieldCheck size={14} className="text-slate-500 mt-0.5 flex-shrink-0" />
            <span className="text-slate-700">
              Keep practising these alongside the current focus:{" "}
              <em>{retainedLabels.join(", ")}</em>.
            </span>
          </li>
        )}
        <li className="flex items-start gap-2"
            data-testid="proof-requirement-variety">
          <MapPin size={14} className="text-slate-500 mt-0.5 flex-shrink-0" />
          <span className="text-slate-700">
            Vary your locations — same roundabout/junction counts max 2×.
          </span>
        </li>
      </ul>
    </div>
  );
}
